import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized, msg, str } from "@lit/localize";
import type { startseite } from "@progressive-development/politik-model";

import { blockStyles } from "../../../common/styles/block-styles.js";
import { registerStyles } from "../../../common/styles/register-styles.js";
import { navigateOnClick } from "../../../common/router/app-navigation.js";
import {
  datumKurzKlartext,
  datumZeitleiste,
  uhrzeitKurz,
  zahl,
} from "../../../common/format/de-format.js";
import {
  ROUTE_AUSSCHUESSE,
  ROUTE_BEZIRKSVERTRETUNGEN,
  ROUTE_DEZERNATE,
  ROUTE_PARTEIEN,
  ROUTE_RAT,
  bezirksvertretungPfad,
} from "../../../config/navigation.js";
import { fraktionenImRat } from "../gremien-uebersicht-daten.js";
import { parteiFarbe } from "../../wahlen/parteien.js";
import type { OrganigrammZahlen } from "../gremien-uebersicht-daten.js";

/** Die eigene Bezirksvertretung — nur im kompakten Modus (Startseite, Merker). */
export interface EigeneBv {
  name: string;
  bezirk: string;
  vorsitz?: string;
  naechsteSitzung?: startseite.TerminEintrag;
}

/**
 * Das Organigramm der Stadtpolitik (FACHKONZEPT-gremien-uebersicht.md, 2.2
 * und 3) — aus Typografie: Versal-Kopf je Spalte, Haarlinie über jedem
 * Eintrag, Kennzahl, Verbindungswort. KEINE KÄSTEN, KEINE FLÄCHEN
 * (KONZEPT-gestaltungssystem.md, 3.3; Artboard „Organigramm ohne Flächen“).
 *
 * ZWEI WELTEN, ZWEI TÖNE: links „Gewählt“ (Rat, Kräfte, Ausschüsse,
 * Bezirksvertretungen — die Sitze in Parteifarben), rechts „Verwaltung“ im
 * Verwaltungston (warmes Schiefer, Regel 2) — nur als Zahl, Linie und
 * Balken, nie als Fläche, nie als Text. Der Oberbürgermeister steht als
 * Scharnier in Tinte mit starker Linie: gewählt, aber Verwaltung.
 *
 * ZWEI AUSPRÄGUNGEN, EIN BAUSTEIN: `kompakt` (Startseite) ohne Kräfte und
 * Ausschüsse, dafür mit der eigenen Bezirksvertretung des gemerkten Gebiets;
 * voll (Übersicht) mit allem. Dasselbe Bild in zwei Größen.
 *
 * Zahlen kommen von außen (`organigrammZahlen`), jeder Eintrag ist ein Weg.
 * Fehlt eine Zahl, fehlt ihr Eintrag — nie 0. Die Verbindungswörter mit
 * linker Linie bleiben: Mobil, wenn die Spalten untereinander stehen, sind
 * sie die eigentliche Lesehilfe.
 */
@customElement("app-organigramm")
@localized()
export class AppOrganigramm extends LitElement {
  @property({ attribute: false }) zahlen: OrganigrammZahlen = {};

  /** Für die Balkenzeile des Rates. */
  @property({ attribute: false }) sitzverteilung?: startseite.SitzverteilungDokument;

  @property({ attribute: false }) parteien: startseite.ParteiEintrag[] = [];

  @property({ type: Boolean, reflect: true }) kompakt = false;

  /** Nur kompakt: die Bezirksvertretung des gemerkten Gebiets. */
  @property({ attribute: false }) eigeneBv?: EigeneBv;

  static override styles = [
    blockStyles,
    registerStyles,
    css`
      :host {
        display: block;
        container-type: inline-size;
        /* Die Linien der Welt: Gewählt in der Haarlinie, Verwaltung in ihrem Ton. */
        --welt-linie: var(--portal-linie);
        --welt-zahl: var(--portal-ink);
      }
      .spalten {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: var(--portal-space-6);
        align-items: start;
      }
      .spalte {
        display: grid;
      }
      .spalte.verwaltung {
        --welt-linie: var(--portal-verwaltung-linie);
        --welt-zahl: var(--portal-verwaltung);
      }
      /* Der Spaltenkopf: Versal, Haarlinie darunter — in der Verwaltung im Verwaltungston. */
      .welt {
        margin: 0 0 var(--portal-space-3);
        padding-bottom: var(--portal-space-2);
        border-bottom: 1px solid var(--welt-linie);
      }
      .verwaltung .welt {
        color: var(--portal-verwaltung);
      }

      /* Der Eintrag: eine Haarlinie darüber, sonst nichts — die ganze Zeile ist der Weg. */
      .eintrag {
        display: block;
        padding: var(--portal-space-3) 0;
        border-top: 1px solid var(--welt-linie);
        color: inherit;
        text-decoration: none;
      }
      a.eintrag:hover .weg,
      a.eintrag:hover .name {
        color: var(--portal-akzent);
      }
      a.eintrag:focus-visible {
        outline: var(--portal-focus-width) solid var(--portal-akzent);
        outline-offset: 2px;
      }
      /* Das Scharnier: gewählt, aber Verwaltung — Tinte mit starker Linie. */
      .eintrag.scharnier {
        border-top: 2px solid var(--portal-ink);
      }
      .eintrag.scharnier .versal {
        color: var(--portal-ink);
      }
      /* Direkt unter dem Spaltenkopf steht schon dessen Linie — der erste Eintrag bringt keine zweite mit. */
      .welt + .eintrag:not(.scharnier) {
        border-top: none;
        padding-top: 0;
      }
      /* Ein Abstand ohne Verbindungswort: die Bezirksvertretungen hängen nicht am Rat. */
      .eintrag.abgesetzt {
        margin-top: var(--portal-space-6);
      }

      .zahl {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: var(--portal-space-2);
        margin: 0;
      }
      .zahl .wert {
        font-family: var(--portal-font-display);
        font-stretch: var(--portal-display-stretch);
        font-weight: var(--portal-display-weight);
        font-size: var(--portal-size-lg);
        line-height: 1;
        font-variant-numeric: tabular-nums;
        color: var(--welt-zahl);
      }
      .zahl .was {
        font-family: var(--portal-font-body);
        font-size: var(--portal-size-md);
      }
      .name {
        font-family: var(--portal-font-display);
        font-stretch: var(--portal-display-stretch);
        font-weight: var(--portal-display-weight);
        font-size: var(--portal-size-md);
        margin: 0;
      }
      .satz {
        margin: var(--portal-space-2) 0 0;
        max-width: 38ch;
        font-size: var(--portal-size-sm);
        color: var(--portal-ink-2);
      }
      .scharnier .satz {
        color: var(--portal-ink);
      }
      /* Das Verbindungswort: Mono, linke Linie in der Farbe der Welt. */
      .verb {
        margin: var(--portal-space-2) 0;
        padding: var(--portal-space-2) 0 var(--portal-space-2) var(--portal-space-3);
        border-left: 1px solid var(--welt-linie);
      }
      /*
       * Der Stapelbalken: Sitze im Rat in Parteifarben (Sitzplan-Ordnung, 1px Fuge),
       * Beschäftigte je Dezernat im Verwaltungston abgestuft (die vier größten, der Rest).
       */
      .balken {
        display: flex;
        gap: 1px;
        height: 6px;
        margin: var(--portal-space-2) 0 var(--portal-space-1);
      }
      .balken i {
        display: block;
        height: 100%;
        background: var(--portal-verwaltung);
      }
      .verwaltung .balken {
        gap: 0;
      }
      .weg {
        display: inline-block;
        margin-top: var(--portal-space-2);
      }
      dl {
        margin: var(--portal-space-2) 0 0;
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 2px var(--portal-space-3);
      }
      dt {
        font-family: var(--portal-font-mono);
        font-size: var(--portal-size-meta);
        color: var(--portal-ink-2);
      }
      dd {
        margin: 0;
        font-size: var(--portal-size-sm);
      }
      @container (max-width: 40rem) {
        .spalten {
          grid-template-columns: minmax(0, 1fr);
          gap: var(--portal-space-8);
        }
      }
    `,
  ];

  private _eintrag(pfad: string, inhalt: unknown, klasse = "") {
    return html`<a
      class="eintrag ${klasse}"
      href=${pfad}
      @click=${(e: Event) => navigateOnClick(e, pfad)}
      >${inhalt}</a
    >`;
  }

  private _verb(text: string) {
    return html`<p class="meta verb">${text}</p>`;
  }

  private _kennzahl(wert: string | number, was: unknown) {
    return html`<p class="zahl">
      <span class="wert">${wert}</span><span class="was">${was}</span>
    </p>`;
  }

  private _renderRat() {
    const z = this.zahlen;
    const sv = this.sitzverteilung;
    if (z.ratSitze === undefined) return nothing;
    return this._eintrag(
      `/${ROUTE_RAT}`,
      html`
        ${this._kennzahl(
          zahl(z.ratSitze) ?? "",
          msg("Sitze im Rat", { id: "app.rat.sitze.titel" }),
        )}
        ${
          sv?.fraktionen?.length
            ? html`<div class="balken" aria-hidden="true">
                ${fraktionenImRat(sv).map(
                  (f) =>
                    html`<i
                      style="width: ${((f.sitze / sv.sitzeGesamt) * 100).toFixed(
                        1,
                      )}%; background: ${parteiFarbe(this.parteien, f.partei)}"
                    ></i>`,
                )}
              </div>`
            : nothing
        }
        <p class="satz">
          ${msg(
            "Die gewählte Vertretung der Stadt — beschließt Haushalt, Satzungen und die großen Vorhaben.",
            { id: "app.organigramm.ratSatz" },
          )}
        </p>
        <span class="weg">${msg("Zum Rat", { id: "app.organigramm.zumRat" })} →</span>
      `,
    );
  }

  /**
   * Die Kräfte im Rat — der Eintrag, der Partei und Fraktion auseinanderhält:
   * gewählt werden Parteien und Wählergruppen, im Rat bilden ihre Mitglieder
   * Fraktionen. Zahlen aus dem Sitzplan, Weg zu den Parteien. Nur voll.
   */
  private _renderKraefte() {
    const k = this.zahlen.kraefteImRat;
    if (this.kompakt || !k) return nothing;
    const teile = [
      k.fraktionen === 1
        ? msg("1 Fraktion", { id: "app.organigramm.fraktion.eine" })
        : msg(str`${k.fraktionen} Fraktionen`, { id: "app.organigramm.fraktion.mehrere" }),
      ...(k.gruppen
        ? [
            k.gruppen === 1
              ? msg("1 Gruppe", { id: "app.organigramm.gruppe.eine" })
              : msg(str`${k.gruppen} Gruppen`, { id: "app.organigramm.gruppe.mehrere" }),
          ]
        : []),
      ...(k.einzelmandate
        ? [
            k.einzelmandate === 1
              ? msg("1 Einzelmandat", { id: "app.organigramm.einzel.eines" })
              : msg(str`${k.einzelmandate} Einzelmandate`, {
                  id: "app.organigramm.einzel.mehrere",
                }),
          ]
        : []),
    ];
    return html`${this._verb(msg("bilden", { id: "app.organigramm.bilden" }))}
    ${this._eintrag(
      `/${ROUTE_PARTEIEN}`,
      html`
        ${this._kennzahl(
          k.fraktionen + k.gruppen + k.einzelmandate,
          msg("Kräfte im Rat", { id: "app.organigramm.kraefte" }),
        )}
        <p class="satz">${teile.join(" · ")}</p>
        <p class="satz">
          ${msg(
            "Gewählt werden Parteien und Wählergruppen; im Rat schließen sich ihre Mitglieder zu Fraktionen zusammen, kleinere bilden Gruppen.",
            { id: "app.organigramm.fraktionSatz" },
          )}
        </p>
        <span class="weg"
          >${msg("Alle Parteien und Wählergruppen", { id: "app.partei.zurUebersicht" })} →</span
        >
      `,
    )}`;
  }

  private _renderAusschuesse() {
    const z = this.zahlen;
    if (this.kompakt || z.ausschuesse === undefined) return nothing;
    return html`${this._verb(msg("bestellt", { id: "app.organigramm.bestellt" }))}
    ${this._eintrag(
      `/${ROUTE_AUSSCHUESSE}`,
      html`
        ${this._kennzahl(
          z.ausschuesse,
          msg("Ausschüsse und Gremien", { id: "app.start.gremien.ausschuesseGremien" }),
        )}
        <p class="satz">
          ${msg(
            "Vom Rat bestellt: bereiten seine Entscheidungen fachlich vor und entscheiden in ihrem Bereich selbst.",
            { id: "app.organigramm.ausschussSatz" },
          )}
        </p>
        <span class="weg">${msg("Alle anzeigen", { id: "app.start.gremien.alleAnzeigen" })} →</span>
      `,
    )}`;
  }

  private _renderBven() {
    const z = this.zahlen;
    if (this.kompakt && this.eigeneBv) return this._renderEigeneBv(this.eigeneBv);
    if (z.bezirksvertretungen === undefined) return nothing;
    return this._eintrag(
      `/${ROUTE_BEZIRKSVERTRETUNGEN}`,
      html`
        ${this._kennzahl(z.bezirksvertretungen, msg("Bezirksvertretungen", { id: "app.menu.bvs" }))}
        <p class="satz">
          ${
            z.bvSitze
              ? msg(str`Direkt gewählt, je ${z.bvSitze} Sitze — eine je Stadtbezirk.`, {
                  id: "app.organigramm.bvSatz",
                })
              : msg("Direkt gewählt — eine je Stadtbezirk.", { id: "app.organigramm.bvSatzOhne" })
          }
        </p>
        <span class="weg"
          >${msg("Alle Bezirksvertretungen", { id: "app.start.gremien.alleBvs" })} →</span
        >
      `,
      "abgesetzt",
    );
  }

  /** Unter den Bezirksvertretungen: die gewählten Kräfte dort — mit Verbinder, wie beim Rat. */
  private _renderParteienInBven() {
    const n = this.zahlen.parteienInBven;
    if (this.kompakt || !n) return nothing;
    return html`${this._verb(msg("vertreten", { id: "app.organigramm.vertreten" }))}
    ${this._eintrag(
      `/${ROUTE_PARTEIEN}`,
      html`
        ${this._kennzahl(
          n,
          msg("Parteien und Wählergruppen in den Bezirksvertretungen", {
            id: "app.organigramm.parteienInBven",
          }),
        )}
        <span class="weg"
          >${msg("Alle Parteien und Wählergruppen", { id: "app.partei.zurUebersicht" })} →</span
        >
      `,
    )}`;
  }

  private _renderEigeneBv(bv: EigeneBv) {
    const s = bv.naechsteSitzung;
    return this._eintrag(
      bezirksvertretungPfad(bv.bezirk),
      html`
        <p class="versal">${msg("Meine Bezirksvertretung", { id: "app.organigramm.meineBv" })}</p>
        <p class="name">${bv.name}</p>
        <dl>
          ${
            bv.vorsitz
              ? html`<dt>${msg("Vorsitz", { id: "app.start.gremien.vorsitz" })}</dt>
                  <dd>${bv.vorsitz}</dd>`
              : nothing
          }
          ${
            s
              ? html`<dt>${msg("Nächste Sitzung", { id: "app.start.gremien.naechsteSitzung" })}</dt>
                  <dd>
                    ${datumZeitleiste(s.start)}, ${uhrzeitKurz(s.start)}
                    ${msg("Uhr", { id: "app.start.gremien.uhr" })}
                  </dd>`
              : nothing
          }
        </dl>
        <span class="weg"
          >${msg("Zur Bezirksvertretung", { id: "app.start.gremien.zurBv" })} →</span
        >
      `,
      "abgesetzt",
    );
  }

  /** Das Scharnier: gewählt, aber Verwaltung. Rolle als Versal, Name und Satz in Tinte. */
  private _renderOb() {
    const ob = this.zahlen.oberbuergermeister;
    if (!ob) return nothing;
    return this._eintrag(
      `/${ROUTE_DEZERNATE}#ob`,
      html`
        <p class="versal">${ob.rolle}</p>
        <p class="satz">
          ${msg(str`${ob.name} — gewählt; führt den Vorsitz im Rat und leitet die Verwaltung.`, {
            id: "app.organigramm.obSatz",
          })}
          ${
            this.zahlen.buergermeisterinnen
              ? msg(str`${this.zahlen.buergermeisterinnen} Bürgermeisterinnen als Vertretung.`, {
                  id: "app.organigramm.bm",
                })
              : nothing
          }
        </p>
      `,
      "scharnier",
    );
  }

  private _renderDezernate() {
    const z = this.zahlen;
    if (z.dezernate === undefined) return nothing;
    return html`${this._verb(msg("leitet", { id: "app.organigramm.leitet" }))}
    ${this._eintrag(
      `/${ROUTE_DEZERNATE}`,
      html`
        ${this._kennzahl(
          z.dezernate,
          html`${msg("Dezernate", { id: "app.start.gremien.dezernate" })}${
            z.aemter
              ? html` <span class="versal"
                  >· ${zahl(z.aemter)} ${msg("Ämter", { id: "app.start.gremien.aemter" })}</span
                >`
              : nothing
          }`,
        )}
        <p class="satz">
          ${msg("Jedes Dezernat fasst die Ämter zusammen, die ein Aufgabengebiet bearbeiten.", {
            id: "app.organigramm.dezernatSatz",
          })}
        </p>
        <span class="weg">${msg("Dezernate und Ämter", { id: "app.dezernate.titel" })} →</span>
      `,
    )}`;
  }

  private _renderBeschaeftigte() {
    const z = this.zahlen;
    if (z.beschaeftigte === undefined) return nothing;
    const anteile = z.beschaeftigteAnteile ?? [];
    // Die vier größten voll bis leise, der Rest am leisesten — die Ordnung liefert die Datenfunktion.
    const staerken = [1, 0.7, 0.5, 0.4, 0.25];
    return this._eintrag(
      `/${ROUTE_DEZERNATE}#beschaeftigte`,
      html`
        ${this._kennzahl(
          zahl(z.beschaeftigte) ?? "",
          msg("Beschäftigte", { id: "app.start.gremien.beschaeftigte" }),
        )}
        ${
          anteile.length
            ? html`<div class="balken" aria-hidden="true">
                ${anteile.map(
                  (a, i) =>
                    html`<i
                      style="width: ${(a * 100).toFixed(1)}%; opacity: ${
                        staerken[Math.min(i, staerken.length - 1)]
                      }"
                    ></i>`,
                )}
              </div>`
            : nothing
        }
        ${
          z.beschaeftigteStichtag
            ? html`<p class="satz">
                ${msg(str`Stichtag ${datumKurzKlartext(z.beschaeftigteStichtag)}`, {
                  id: "app.organigramm.stichtag",
                })}
              </p>`
            : nothing
        }
      `,
    );
  }

  override render() {
    return html`
      <div class="spalten">
        <div class="spalte gewaehlt">
          <p class="versal welt">
            ${msg("Gewählt · alle fünf Jahre", { id: "app.organigramm.gewaehltKopf" })}
          </p>
          ${this._renderRat()} ${this._renderKraefte()} ${this._renderAusschuesse()}
          ${this._renderBven()} ${this._renderParteienInBven()}
        </div>
        <div class="spalte verwaltung">
          <p class="versal welt">
            ${msg("Verwaltung · vom Rat bestellt", { id: "app.organigramm.verwaltungKopf" })}
          </p>
          ${this._renderOb()} ${this._renderDezernate()} ${this._renderBeschaeftigte()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-organigramm": AppOrganigramm;
  }
}
